import { useCallback, useEffect, useState } from 'react';
import { api } from './adminApi';
import { btnGhost, btnPrimary, cardCls } from './AdminPanel';

interface MediaItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  fileUrl: string;
  thumbnailUrl?: string;
  mimeType?: string;
  fileSize?: string;
  originalName?: string;
  authorName?: string;
  createdAt: string;
}

export default function AdminMedia({ onToast }: { onToast: (msg: string) => void }) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const res = await api<{ media: MediaItem[] }>('/api/media');
    if (res.ok && res.data) setItems(res.data.media);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (item: MediaItem) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    const res = await api(`/api/media/${item.id}`, { method: 'DELETE' });
    if (res.ok) { onToast('Media deleted'); await load(); } else onToast(res.error || 'Delete failed');
  };

  const upload = async (file: File) => {
    setUploading(true);
    const body = new FormData();
    body.append('file', file);
    const res = await api('/api/media/upload', { method: 'POST', body });
    setUploading(false);
    if (res.ok) { onToast('Media uploaded'); await load(); } else onToast(res.error || 'Upload failed');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading media…</p>;

  return (
    <div className={cardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold">Media library ({items.length})</h2>
        <div className="flex gap-2">
          <button onClick={load} className={btnGhost}>↻ Refresh</button>
          <label className={btnPrimary + ' cursor-pointer'}>
            {uploading ? 'Uploading…' : '+ Upload'}
            <input type="file" className="hidden" accept="image/*,video/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
          </label>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {items.map((item) => (
          <div key={item.id} className="overflow-hidden rounded-lg border border-white/10 bg-black/20">
            {item.thumbnailUrl || (item.mimeType && item.mimeType.startsWith('image/')) ? (
              <img src={item.thumbnailUrl || item.fileUrl} alt={item.title} className="h-28 w-full object-cover" loading="lazy" />
            ) : (
              <div className="grid h-28 place-items-center bg-white/5 text-2xl">🎞</div>
            )}
            <div className="p-2">
              <div className="truncate text-xs font-bold">{item.title}</div>
              <div className="text-[10px] text-gray-500">{item.category} · {item.authorName} · {new Date(item.createdAt).toLocaleDateString()}</div>
              <button onClick={() => remove(item)} className="mt-1 w-full rounded border border-red-500/30 py-1 text-[11px] font-semibold text-red-400 hover:bg-red-500/10">Delete</button>
            </div>
          </div>
        ))}
        {!items.length && <p className="col-span-full py-6 text-center text-xs text-gray-500">No media yet.</p>}
      </div>
    </div>
  );
}